/* global window */
// ============================================================
// FLERKLIENT-LAGER — én plass per åpen klient
// ============================================================
//
// Appen har til nå hatt nøyaktig ett aktivt regnskap i minnet: `runtime.appState`.
// Regnskapsførerflaten trenger flere klienter åpne samtidig (faner), og da må hver
// klient ha sin egen state, sine egne lyttere og sin egen lagringstimer.
//
// Denne modulen eier BARE bokføringen av hvilke klienter som er åpne og hva som
// hører til hver av dem. Den kjenner ikke Firestore. `backend.jsx` gir den en
// `unsubscribe` per klient og kaller `closeClient` når en fane lukkes — da får den
// funksjonen tilbake og river ned lytteren.
//
// Se docs/features/regnskapsbyra-workspace-backlog.md, beslutning 9.4.

(function initMultiClientStore(global) {
  const DEFAULT_MAX_OPEN_CLIENTS = 6;

  const ensureId = (value) => (typeof value === 'string' ? value.trim() : '');

  const createSlot = (businessId, now) => ({
    businessId,
    appState: null,
    appStateRevision: '',
    persistedAppStateRevision: '',
    persistedShardRevisions: {},
    // null = ikke forsøkt lest ennå. false = lesingen feilet, og da skal
    // personaldokumentet ikke skrives. Samme semantikk som før flerklient.
    personnelStateReadable: null,
    loading: true,
    error: null,
    unsubscribe: null,
    saveTimer: null,
    openedAt: now,
    lastUsedAt: now,
  });

  const createMultiClientStore = ({
    maxOpenClients = DEFAULT_MAX_OPEN_CLIENTS,
    now = () => Date.now(),
  } = {}) => {
    const slots = new Map();
    let activeId = null;
    let sequence = 0;

    // Date.now() har millisekundoppløsning. To bytter i samme millisekund ville fått
    // samme «sist brukt», og utkastingen ville vært vilkårlig. Sekvensnummeret gjør
    // rekkefølgen entydig uansett klokke.
    const stamp = () => {
      sequence += 1;
      return { at: now(), seq: sequence };
    };

    const touch = (slot) => {
      const marked = stamp();
      slot.lastUsedAt = marked.at;
      slot.lastUsedSeq = marked.seq;
      return slot;
    };

    const openClient = (rawId) => {
      const businessId = ensureId(rawId);
      if (!businessId) return null;
      const existing = slots.get(businessId);
      if (existing) return touch(existing);
      const marked = stamp();
      const slot = createSlot(businessId, marked.at);
      slot.lastUsedSeq = marked.seq;
      slots.set(businessId, slot);
      return slot;
    };

    const getSlot = (rawId) => slots.get(ensureId(rawId)) || null;

    const hasClient = (rawId) => slots.has(ensureId(rawId));

    const setActive = (rawId) => {
      const businessId = ensureId(rawId);
      if (!businessId) {
        activeId = null;
        return null;
      }
      const slot = openClient(businessId);
      activeId = businessId;
      return slot;
    };

    const getActiveSlot = () => (activeId ? slots.get(activeId) || null : null);

    // Lukking returnerer plassen slik at kalleren kan rive ned lytteren og
    // lagringstimeren. Lageret rører aldri dem selv — det vet ikke hva de er.
    const closeClient = (rawId) => {
      const businessId = ensureId(rawId);
      const slot = slots.get(businessId);
      if (!slot) return null;
      slots.delete(businessId);
      if (activeId === businessId) activeId = null;
      return slot;
    };

    const closeAll = () => {
      const closed = [...slots.values()];
      slots.clear();
      activeId = null;
      return closed;
    };

    // Hver åpen klient holder en Firestore-lytter og et helt regnskap i minnet. Uten
    // et tak vokser en lang arbeidsdag med fanebytter til den spiser minnet.
    // Den aktive klienten kastes aldri ut.
    const evictOverflow = () => {
      if (slots.size <= maxOpenClients) return [];
      const candidates = [...slots.values()]
        .filter((slot) => slot.businessId !== activeId)
        .sort((a, b) => (a.lastUsedSeq || 0) - (b.lastUsedSeq || 0));
      const evicted = [];
      let overflow = slots.size - maxOpenClients;
      for (const slot of candidates) {
        if (overflow <= 0) break;
        slots.delete(slot.businessId);
        evicted.push(slot);
        overflow -= 1;
      }
      return evicted;
    };

    const listOpen = () => [...slots.values()].map((slot) => ({
      businessId: slot.businessId,
      loading: slot.loading,
      hasState: slot.appState != null,
      error: slot.error,
      isActive: slot.businessId === activeId,
      lastUsedAt: slot.lastUsedAt,
    }));

    return {
      openClient,
      getSlot,
      hasClient,
      setActive,
      getActiveSlot,
      closeClient,
      closeAll,
      evictOverflow,
      listOpen,
      get activeId() {
        return activeId;
      },
      get size() {
        return slots.size;
      },
      get maxOpenClients() {
        return maxOpenClients;
      },
    };
  };

  global.PlummeMultiClientStore = { createMultiClientStore, DEFAULT_MAX_OPEN_CLIENTS };
}(typeof window !== 'undefined' ? window : globalThis));
